import { Close, DeleteOutlineOutlined, EditOutlined, Search } from '@mui/icons-material'
import { Avatar, Box, Button, Chip, IconButton, InputAdornment, Pagination, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../Redux/store';
import { toast } from 'react-toastify';
import { ApiGetProducts, ApiUpdateProductStatus } from '../../../Core/Apicall';
import { Product } from '../../../types/Products';
import { isTypeNode } from 'typescript';

function Productlist() {

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate()
    const [productList, setProductList] = useState<Product[]>()
    const [total, setTotal] = useState<number>(-1)
    const { userDetails, } = useSelector((state: RootState) => state.global)
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [search, setSearch] = useState('')


    const filteredProducts = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return productList ?? []
        return (productList ?? []).filter((c) =>
            [
                c.productName,
                c.productCode,
                c.category?.categoryName,
                c.base_price,
            ].some((v) => v?.toLowerCase().includes(term))
        )
    }, [productList, search])


    const getproductList = async () => {
        setProductList([])
        try {
            const data = `?offset=${page * rowsPerPage}&limit=${rowsPerPage}`
            const res = await ApiGetProducts(data)
            if (res?.status || res?.success) {
                setProductList(res?.data)
                setTotal(res?.total)
            }

        } catch (error: any) {
            toast.error(error?.message || "Something went wrong")
        }
    }

    useEffect(() => {
        getproductList()
    }, [page])


    const handleClose = () => {
        setSelectedProduct(null)
        setOpenDelete(false)
    }


    const handleStatusChange = async (productId: number, isActive: boolean) => {
        const isConfirm = window.confirm("Are you sure you want to change product status ?")
        if (isConfirm) {
            try {
                const data = `?productId=${productId}`
                const res = await ApiUpdateProductStatus(data)
                getproductList()
            } catch (error: any) {
                toast.error(error?.message || "Something Went Wrong")
            }
        }
    }

    return (
        <Box className='p-lg-3 p-xl-4 p-2'>
            <Box className='d-flex mb-4 gap-3 flex-column flex-md-row align-items-start align-items-md-center justify-content-between'>
                <Box>
                    <Typography variant='h5' className='fw-bold' sx={{ lineHeight: 'normal' }}>All Products</Typography>
                    <Typography variant='body2'>Manage all your platform products</Typography>
                </Box>
                <Box className='d-flex align-items-center gap-2 text-field justify-content-md-end justify-content-between' sx={{ width: { xs: "100%", md: 'auto' } }}>
                    <TextField
                        className='m-0 p-0'
                        placeholder='Search Product...'
                        sx={{ minWidth: { xs: "250px", md: '300px' } }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start" className='me-0'>
                                        <Search fontSize="small" className='m-0 p-0' />
                                    </InputAdornment>
                                ),
                                endAdornment: search && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearch('')}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    {userDetails?.role === 'client' && (<Button className='btn-main px-3 py-2' onClick={() => { navigate(`/${userDetails?.role}/products/add-or-update-product`) }}>+ Add Product</Button>)}
                </Box>
            </Box>
            <Box className=''>
                <TableContainer
                    className='bg-white'
                    sx={{
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        // overflow: "hidden",
                    }}
                >
                    <Table sx={{ tableLayout: "fixed" }}>
                        <TableHead>
                            <TableRow
                                sx={{
                                    bgcolor: "var(--primary-light-color)",

                                    "& th": {
                                        fontWeight: 600,
                                        color: "#555",
                                        py: 2,
                                        borderBottom: "1px solid #E5E7EB",
                                    },
                                }}
                            >
                                <TableCell sx={{ width: 120 }}>
                                    <Typography variant="caption">Image</Typography>
                                </TableCell>

                                <TableCell sx={{ width: 420 }}>
                                    <Typography variant="caption">Product Name</Typography>
                                </TableCell>

                                <TableCell sx={{ width: 150 }}>
                                    <Typography variant="caption">Product Code</Typography>
                                </TableCell>

                                <TableCell sx={{ width: 180 }}>
                                    <Typography variant="caption">Category</Typography>
                                </TableCell>
                                <TableCell sx={{ width: 180 }}>
                                    <Typography variant="caption">Status</Typography>
                                </TableCell>

                                <TableCell sx={{ width: 100 }} className='text-center'>
                                    <Typography variant="caption">Price</Typography>
                                </TableCell>

                                <TableCell sx={{ width: 90 }} className='text-center'>
                                    <Typography variant="caption" className='text-center'>Variants</Typography>
                                </TableCell>

                                {userDetails?.role === 'client' && (
                                    <TableCell sx={{ width: 100 }} className='text-center'>
                                        <Typography variant="caption" className='text-center'>Actions</Typography>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredProducts?.map((row: Product) => (
                                <TableRow
                                    key={row.productId}
                                    hover
                                    sx={{
                                        "& td": {
                                            py: 2,
                                            verticalAlign: "middle",
                                            borderBottom: "1px solid #F1F1F1",
                                        },
                                    }}
                                >
                                    {/* Image */}
                                    <TableCell>
                                        <Box
                                            sx={{
                                                width: 100,
                                                height: 50,
                                                overflow: "hidden",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: "#fff",
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={row?.media?.[0]?.media_url}
                                                alt={row.productName}
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Box>
                                    </TableCell>

                                    {/* Product Name */}
                                    <TableCell>
                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {row.productName}
                                        </Typography>
                                    </TableCell>

                                    {/* Product Code */}
                                    <TableCell>
                                        {row.productCode}
                                    </TableCell>

                                    {/* Category */}
                                    <TableCell>
                                        {row.category.categoryName}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.is_active ? "Active" : "Inactive"}
                                            color={row.is_active ? "success" : "error"}
                                            size="small"
                                        />
                                    </TableCell>

                                    {/* Price */}
                                    <TableCell className='text-center'>
                                        ₹{row.discounted_price}
                                    </TableCell>

                                    {/* Variants */}
                                    <TableCell className='text-center'>
                                        {row.variants.length}
                                    </TableCell>

                                    {/* Actions */}
                                    {userDetails?.role === 'client' && (
                                        <TableCell>
                                            <Stack direction="row" className='justify-content-center align-items-center' spacing={1}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        navigate(
                                                            `/${userDetails?.role}/products/add-or-update-product/${row.productId}`
                                                        )
                                                    }
                                                >
                                                    <EditOutlined className='text-main' />
                                                </IconButton>

                                                {/* <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedProduct(row);
                                                        setOpenDelete(true);
                                                    }}
                                                >
                                                    <DeleteOutlineOutlined className='text-danger' />
                                                </IconButton> */}
                                                <Switch
                                                    size="small"
                                                    className='m-0'
                                                    checked={row.is_active}
                                                    onChange={(event) => {
                                                        handleStatusChange(
                                                            row.productId,
                                                            event.target.checked
                                                        );
                                                    }}
                                                />
                                            </Stack>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Box className='p-3 d-flex justify-content-end'>
                {total > rowsPerPage &&
                    <Pagination
                        page={page + 1}
                        count={Math.ceil(total / rowsPerPage)}
                        onChange={(_, value) => setPage(value - 1)}
                        shape="rounded"
                        // color="primary"
                        sx={{
                            "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: 'var(--primary-color)',
                                color: 'white'
                            }
                        }}
                        showFirstButton={false}
                        showLastButton={false}
                        siblingCount={1}
                        boundaryCount={1}
                    />}
            </Box>
        </Box>
    )
}

export default Productlist
